import * as z from "zod";
import { auditChangeDto, flag, pagination, validationIssue } from "../index";
import { joorChangeDecisions, joorOrderStatuses, joorWaitingReasons } from "./enums";
import { reason } from "./fields";

// Orders as Joor has them (I-14, ADR-111; 50-api/sales.md §2.3): the latest state of each, and why one waits.

const joorOrderFilters = {
  /** Only the ones whose latest state is not on a sales order. */
  waiting: flag.optional(),
  reason: z.enum(joorWaitingReasons).optional(),
  /** The order of Joor a sales order is: what its page reads to decide a change that waits (B-31, ADR-124). */
  salesOrder: z.uuid().optional(),
  /** Part of the number of the order on Joor or of the name of the retailer, case-insensitive. */
  q: z.string().trim().min(1).max(60).optional(),
};

/** Latest received first. */
export const listJoorOrdersInput = z.object({
  ...joorOrderFilters,
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListJoorOrdersInput = z.input<typeof listJoorOrdersInput>;

/** How many orders the list has with the same filters, and since when the oldest is untouched (ADR-086). */
export const countJoorOrdersInput = z.object(joorOrderFilters);
export type CountJoorOrdersInput = z.input<typeof countJoorOrdersInput>;

/**
 * The state kept of an order of Joor is applied again, as when it arrived: what was missing may be there now.
 * `customerOrgId` says by hand whose the order is, when its retailer matched no customer: the next orders of that
 * retailer follow.
 */
export const applyJoorOrderInput = z.object({ joorOrderId: z.uuid(), customerOrgId: z.uuid().optional() });
export type ApplyJoorOrderInput = z.input<typeof applyJoorOrderInput>;
export const joorOrderParams = applyJoorOrderInput.pick({ joorOrderId: true });
export const applyJoorOrderBody = applyJoorOrderInput.omit({ joorOrderId: true });

/**
 * B-31, ADR-115: the change of Joor that waits on an allocated order is applied, with the allocation that follows it,
 * or dismissed; either way with the reason, in the history of the sales order.
 */
export const decideJoorChangeInput = z.object({
  joorOrderId: z.uuid(),
  decision: z.enum(joorChangeDecisions),
  reason,
});
export type DecideJoorChangeInput = z.input<typeof decideJoorChangeInput>;
export const decideJoorChangeBody = decideJoorChangeInput.omit({ joorOrderId: true });

/** An order as Joor last sent it, with the sales order it is, or why it is not one yet. */
export const joorOrderDto = z
  .object({
    /** Id of the row: what `applyJoorOrder` names. */
    id: z.uuid(),
    /** Id of the order on Joor. */
    joorOrderId: z.string(),
    orderNumber: z.string(),
    customerPoNumber: z.string().nullable(),
    status: z.enum(joorOrderStatuses),
    /** When the order last changed on Joor. */
    joorUpdatedAt: z.iso.datetime(),
    brandOrgId: z.uuid().nullable(),
    brand: z.string().nullable(),
    customerJoorId: z.string(),
    /** Name of the retailer, as Joor has it. */
    customerName: z.string(),
    customerOrgId: z.uuid().nullable(),
    customer: z.string().nullable(),
    salesOrderId: z.uuid().nullable(),
    soNumber: z.string().nullable(),
    waitingReason: z.enum(joorWaitingReasons).nullable(),
    /** Everything missing for the state to be applied: `row` is the index of a line of the order, from 0. */
    issues: z.array(validationIssue),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "JoorOrder" });

/** What applying a change of Joor that waits would do to its sales order (B-31, ADR-124): read, nothing written. */
export const previewJoorChangeInput = z.object({ joorOrderId: z.uuid() });
export type PreviewJoorChangeInput = z.input<typeof previewJoorChangeInput>;

/** A line of the sales order as the change leaves it, with what it keeps of its allocation. */
const joorChangeLineDto = z.object({
  /** The number the line has, or takes once created. */
  lineNo: z.number().int(),
  action: z.enum(["create", "update", "cancel"]),
  skuCode: z.string().nullable(),
  styleCode: z.string().nullable(),
  colour: z.string().nullable(),
  size: z.string().nullable(),
  /** Ordered units, price and discount, before and after. */
  changes: z.array(auditChangeDto),
  qtyAllocated: z.number().int(),
  qtyAllocatedAfter: z.number().int(),
});

/**
 * The latest state of Joor against the sales order as it is, with the rules of the application. `effect`:
 * - `update`: the header and the lines change as listed, and the allocation follows them;
 * - `cancel`: Joor cancelled the order, and applying cancels it;
 * - `none`: the state says what the order has, and applying changes nothing;
 * - `wait`: the state cannot be applied, for what `issues` names (a SKU Nexxt does not have, an order locked).
 */
export const joorChangeDto = z
  .object({
    /** Id of the row of the order of Joor. */
    id: z.uuid(),
    salesOrderId: z.uuid(),
    soNumber: z.string(),
    /** When the order last changed on Joor. */
    joorUpdatedAt: z.iso.datetime(),
    effect: z.enum(["update", "cancel", "none", "wait"]),
    issues: z.array(validationIssue),
    /** The fields of the header that change, values as codes and text. */
    header: z.array(auditChangeDto),
    lines: z.array(joorChangeLineDto),
    /** The company that sells changes, or the order is cancelled: every unit allocated goes back to the warehouse. */
    releasesAll: z.boolean(),
  })
  .meta({ id: "JoorChange" });
export type JoorChange = z.output<typeof joorChangeDto>;
